import { ChevronDownIcon, XMarkIcon } from '@heroicons/react/20/solid';
import * as Popover from '@radix-ui/react-popover';
import React from 'react';

import { PopoverContent } from './Animations';
import { cn } from './cn';
import { FormField, type FormFieldProps } from './FormField';

export interface FormDropdownOption {
  key: string;
  text: string;
  disabled?: boolean;
  /**
   * Optional group id. Options are ordered by it (ascending; ungrouped last) so each group is
   * contiguous under one sticky header, preserving within-group order. A number: cheap to compare and
   * primitive-shared across options. The visible header label is resolved via `renderGroupLabel`; with
   * no `renderGroupLabel`, `group` has no effect (options render in their given order).
   */
  group?: number;
  /** Optional small muted line rendered under `text` */
  secondaryText?: string;
}

export interface FormDropdownProps extends Omit<FormFieldProps, 'children'> {
  options: FormDropdownOption[];
  selectedKey?: string;
  placeholder?: string;
  disabled?: boolean;
  onChange?: (option: FormDropdownOption | null) => void;
  dropdownClassName?: string;
  multiSelect?: boolean;
  selectedKeys?: string[];
  onMultiSelectChange?: (selectedKeys: string[]) => void;
  clearButton?: boolean;
  /**
   * How a multi-select renders its current value in the trigger. `'count'` (default) shows "N selected";
   * `'pills'` shows each selection as a removable chip (like `TextListEditor`). Ignored unless `multiSelect`.
   */
  multiSelectDisplay?: 'count' | 'pills';
  /**
   * Resolve an option's `group` id into a sticky header label (a `ReactNode`, so it can be a translated
   * element). Supplying this turns on grouping: options are sorted by `group` (ungrouped last) and a
   * header renders at each group boundary. Without it, options render in their given order.
   */
  renderGroupLabel?: (group: number) => React.ReactNode;
}

export const FormDropdown = ({
  options,
  selectedKey,
  placeholder = 'Select...',
  disabled,
  onChange,
  dropdownClassName,
  multiSelect,
  selectedKeys = [],
  onMultiSelectChange,
  clearButton,
  multiSelectDisplay = 'count',
  renderGroupLabel,
  label,
  description,
  errorMessage,
  successMessage,
  required,
  className,
  id,
}: FormDropdownProps) => {
  const [open, setOpen] = React.useState(false);
  const generatedId = React.useId();
  const dropdownId = id || generatedId;
  // When grouping is active, order options by group so each group is contiguous (stable sort keeps
  // within-group order; ungrouped sink to the end). Skipped without `renderGroupLabel` — reordering
  // options with no visible header would be surprising. Memoized as a nicety; callers passing a fresh
  // `options` array each render (common) still re-sort, but the cost is trivial for dropdown sizes.
  const orderedOptions = React.useMemo(
    () => (renderGroupLabel ? [...options].sort((a, b) => (a.group ?? Infinity) - (b.group ?? Infinity)) : options),
    [options, renderGroupLabel]
  );
  const selectedOption = options.find((o) => o.key === selectedKey);
  const hasSelection = multiSelect ? selectedKeys.length > 0 : Boolean(selectedOption);
  const showClearButton = Boolean(clearButton) && hasSelection && !disabled;
  const showPills = multiSelect && multiSelectDisplay === 'pills';

  const displayText = multiSelect
    ? selectedKeys.length
      ? `${selectedKeys.length} selected`
      : placeholder
    : selectedOption?.text || placeholder;

  const handleClear = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    event.preventDefault();
    if (multiSelect) {
      onMultiSelectChange?.([]);
    }
    onChange?.(null);
  };

  // When rendered inside a modal Sheet/Dialog, react-remove-scroll blocks native wheel on this
  // portaled popover (it lives outside the dialog's scroll shard). Drive the scroll manually.
  // A callback ref (not useEffect) is required: Radix mounts the popover via Presence in a later
  // commit, so a ref read in an effect would still be null when the effect runs.
  const wheelCleanup = React.useRef<(() => void) | undefined>(undefined);
  const scrollRefCallback = React.useCallback((node: HTMLDivElement | null) => {
    wheelCleanup.current?.();
    wheelCleanup.current = undefined;
    if (!node) return;
    const onWheel = (event: WheelEvent) => {
      node.scrollTop += event.deltaY;
      event.preventDefault();
    };
    node.addEventListener('wheel', onWheel, { passive: false });
    wheelCleanup.current = () => node.removeEventListener('wheel', onWheel);
  }, []);

  const handleSelect = (option: FormDropdownOption) => {
    if (multiSelect) {
      const newKeys = selectedKeys.includes(option.key)
        ? selectedKeys.filter((k) => k !== option.key)
        : [...selectedKeys, option.key];
      onMultiSelectChange?.(newKeys);
    } else {
      onChange?.(option);
      setOpen(false);
    }
  };

  return (
    <FormField
      label={label}
      description={description}
      errorMessage={errorMessage}
      successMessage={successMessage}
      required={required}
      className={className}
      id={dropdownId}
    >
      <Popover.Root open={open} onOpenChange={setOpen}>
        <Popover.Trigger asChild>
          {showPills ? (
            // Pills variant renders removable chips, so the trigger is a div (a <button> can't contain buttons).
            <div
              id={dropdownId}
              role="button"
              tabIndex={disabled ? -1 : 0}
              aria-disabled={disabled}
              aria-haspopup="listbox"
              aria-expanded={open}
              onKeyDown={(event) => {
                if (!disabled && (event.key === 'Enter' || event.key === ' ')) {
                  event.preventDefault();
                  setOpen((prev) => !prev);
                }
              }}
              className={cn(
                'flex w-full flex-wrap items-center gap-1.5 rounded-md border bg-surface px-2 py-1.5 text-left text-sm text-text-primary transition-colors duration-200',
                'focus:outline-none focus:ring-2 focus:ring-info-500 focus:border-info-500',
                errorMessage ? 'border-danger-400' : 'border-gray-300',
                disabled ? 'bg-gray-50 text-gray-500 cursor-not-allowed' : 'cursor-pointer hover:border-gray-400',
                dropdownClassName
              )}
            >
              {selectedKeys.length ? (
                selectedKeys.map((key) => {
                  const text = options.find((o) => o.key === key)?.text ?? key;
                  return (
                    <span
                      key={key}
                      className="inline-flex items-center gap-1 rounded-full bg-info-100 py-0.5 pl-2.5 pr-1 type-label text-info-700"
                    >
                      {text}
                      {!disabled && (
                        <button
                          type="button"
                          tabIndex={-1}
                          aria-label={`Remove ${text}`}
                          onMouseDown={(event) => event.preventDefault()}
                          onClick={(event) => {
                            event.stopPropagation();
                            onMultiSelectChange?.(selectedKeys.filter((k) => k !== key));
                          }}
                          className="flex h-4 w-4 items-center justify-center rounded-full border-none bg-transparent text-info-700 transition-colors duration-150 hover:bg-info-200"
                        >
                          <XMarkIcon className="h-3 w-3" />
                        </button>
                      )}
                    </span>
                  );
                })
              ) : (
                <span className="px-1 py-0.5 text-gray-400">{placeholder}</span>
              )}
              <span className="ml-auto flex shrink-0 items-center gap-1 self-center">
                {showClearButton && (
                  <span
                    role="button"
                    tabIndex={0}
                    aria-label="Clear selection"
                    className="text-gray-400 hover:text-gray-600 cursor-pointer p-0.5 rounded-md hover:bg-gray-100 transition-colors"
                    onMouseDown={(event) => event.preventDefault()}
                    onClick={handleClear}
                    onKeyDown={(event) => {
                      if (event.key === 'Enter' || event.key === ' ') {
                        handleClear(event as unknown as React.MouseEvent<HTMLButtonElement>);
                      }
                    }}
                  >
                    <XMarkIcon className="h-4 w-4" />
                  </span>
                )}
                <ChevronDownIcon
                  className={cn('h-4 w-4 text-gray-400 transition-transform duration-200', open && 'rotate-180')}
                />
              </span>
            </div>
          ) : (
            <button
              id={dropdownId}
              disabled={disabled}
              className={cn(
                'flex w-full items-center justify-between rounded-md border bg-surface px-3 py-2 text-left text-sm text-text-primary transition-colors duration-200',
                'focus:outline-none focus:ring-2 focus:ring-info-500 focus:border-info-500',
                errorMessage ? 'border-danger-400' : 'border-gray-300',
                disabled ? 'bg-gray-50 text-gray-500 cursor-not-allowed' : 'cursor-pointer hover:border-gray-400',
                !selectedOption && !multiSelect && 'text-gray-400',
                dropdownClassName
              )}
            >
              <span className="truncate">{displayText}</span>
              <span className="flex shrink-0 items-center gap-1">
                {showClearButton && (
                  <span
                    role="button"
                    tabIndex={0}
                    aria-label="Clear selection"
                    className="text-gray-400 hover:text-gray-600 cursor-pointer p-0.5 rounded-md hover:bg-gray-100 transition-colors"
                    onClick={handleClear}
                    onKeyDown={(event) => {
                      if (event.key === 'Enter' || event.key === ' ') {
                        handleClear(event as unknown as React.MouseEvent<HTMLButtonElement>);
                      }
                    }}
                  >
                    <XMarkIcon className="h-4 w-4" />
                  </span>
                )}
                <ChevronDownIcon
                  className={cn('h-4 w-4 text-gray-400 transition-transform duration-200', open && 'rotate-180')}
                />
              </span>
            </button>
          )}
        </Popover.Trigger>
        <Popover.Portal>
          <Popover.Content
            className="z-50 w-[var(--radix-popover-trigger-width)] rounded-lg border border-border bg-surface py-1 shadow-lg"
            sideOffset={4}
            align="start"
            asChild
          >
            <PopoverContent>
              {/* Scroll on an inner div (not the transformed motion element) so the mouse wheel works. */}
              <div ref={scrollRefCallback} className="max-h-60 overflow-y-auto">
                {orderedOptions.map((option, index) => (
                  <React.Fragment key={option.key}>
                    {/* Header on a group boundary. `!== undefined` (not truthiness) so group id `0` still renders. */}
                    {option.group !== undefined &&
                      option.group !== orderedOptions[index - 1]?.group &&
                      renderGroupLabel?.(option.group) != null && (
                        <div className="sticky top-0 z-10 bg-surface px-3 pb-1 pt-2 type-table-header text-text-muted">
                          {renderGroupLabel(option.group)}
                        </div>
                      )}
                    <button
                      disabled={option.disabled}
                      onClick={() => handleSelect(option)}
                      className={cn(
                        'flex w-full items-center gap-2 px-3 py-2 text-left text-sm transition-colors duration-150 bg-transparent border-none cursor-pointer',
                        option.disabled ? 'text-gray-300 cursor-not-allowed' : 'text-gray-700 hover:bg-gray-50',
                        !multiSelect && option.key === selectedKey && 'bg-gray-50 font-medium text-info-600',
                        multiSelect && selectedKeys.includes(option.key) && 'bg-gray-50 font-medium text-info-600'
                      )}
                    >
                      {multiSelect && (
                        <span
                          className={cn(
                            'flex h-4 w-4 shrink-0 items-center justify-center rounded border transition-colors duration-150',
                            selectedKeys.includes(option.key)
                              ? 'border-info-500 bg-info-500 text-white'
                              : 'border-gray-300'
                          )}
                        >
                          {selectedKeys.includes(option.key) && (
                            <svg
                              viewBox="0 0 12 12"
                              className="h-3 w-3"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth="2"
                            >
                              <path d="M2 6l3 3 5-5" />
                            </svg>
                          )}
                        </span>
                      )}
                      <span className="flex min-w-0 flex-col">
                        <span className="truncate">{option.text}</span>
                        {option.secondaryText && (
                          <span className="type-caption truncate text-text-muted">{option.secondaryText}</span>
                        )}
                      </span>
                    </button>
                  </React.Fragment>
                ))}
                {!options.length && <div className="px-3 py-2 text-sm text-gray-400">No options available</div>}
              </div>
            </PopoverContent>
          </Popover.Content>
        </Popover.Portal>
      </Popover.Root>
    </FormField>
  );
};
